import './index.scss'
import '@mdxeditor/editor/style.css'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  InsertImage,
  InsertTable,
  ListsToggle,
  MDXEditor,
  UndoRedo,
  headingsPlugin,
  imagePlugin,
  linkDialogPlugin,
  linkPlugin,
  listsPlugin,
  markdownShortcutPlugin,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin
} from '@mdxeditor/editor'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import NetworkController from '@data/controller/NetworkController'
import TrainingController from '@data/controller/TrainingController'
import useDarkMode, { Theme } from '@hooks/useDarkMode'
import {
  TrainingResource,
  TrainingResourceAttributes,
  TrainingVideo,
  videoURLFromResponse
} from '@interfaces/training.interface'

import closeIcon from 'url:@icon/close.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';

const DESCRIPTION_LIMIT = 300;

export default function LessonEditorModal(props: {
  isOpen: boolean,
  moduleID: string,
  lesson?: TrainingResource | null,
  close: () => void,
  onSaved: () => void
}) {
  const auth = useAuth();
  const { t } = useTranslation(['lessonEditor', 'volunteering']);
  const theme = useDarkMode();
  const attributes = props.lesson?.attributes;

  const [name, setName] = useState(attributes?.name ?? '');
  const [description, setDescription] = useState(attributes?.description ?? '');
  const [text, setText] = useState(attributes?.text ?? '');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [pickedThumbnailURL, setPickedThumbnailURL] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The video upload takes over the modal once the Lesson itself is saved, so
  // a long upload reads as progress rather than a stalled save.
  const [uploadingLessonID, setUploadingLessonID] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const isUploading = uploadingLessonID !== null;

  const video = attributes?.video ?? null;
  const isEditing = Boolean(props.lesson);

  // A new Lesson needs its required fields filled in; an existing one needs an
  // actual change to save.
  const meetsRequirements = name.trim().length > 0;
  const hasChanges =
    name !== (attributes?.name ?? '') ||
    description !== (attributes?.description ?? '') ||
    text !== (attributes?.text ?? '') ||
    videoFile !== null ||
    thumbnailFile !== null;
  const canSave = meetsRequirements && (!isEditing || hasChanges) && !isSaving;

  // A picked thumbnail previews from a local object URL; an existing video
  // falls back to whatever the API already has.
  useEffect(() => {
    if (!thumbnailFile) {
      setPickedThumbnailURL(null);
      return;
    }

    const objectURL = URL.createObjectURL(thumbnailFile);
    setPickedThumbnailURL(objectURL);

    return () => {
      URL.revokeObjectURL(objectURL);
    };
  }, [thumbnailFile]);

  const previewVideo = async () => {
    if (!video || video.type === 'PROCESSING' || video.type === 'FAILED') {
      return;
    }

    // Opened up front so the tab is tied to the click rather than to the
    // response, which a popup blocker would refuse.
    const previewTab = window.open('', '_blank');

    if (video.type === 'HOSTED') {
      if (previewTab) {
        previewTab.location.href = video.url;
      }

      return;
    }

    if (!auth.churchId || !props.lesson) {
      previewTab?.close();
      setError(t('volunteering:errors.missingChurch'));
      return;
    }

    setIsPreviewing(true);
    setError(null);

    try {
      const response = await TrainingController.fetchVideoURL(
        auth.churchId,
        props.moduleID,
        props.lesson.id,
        auth
      );

      const url = response.error ? null : videoURLFromResponse(response.body);

      if (!url) {
        previewTab?.close();
        setError(response.error || t('previewFailed'));
        return;
      }

      if (previewTab) {
        previewTab.location.href = url;
      }
    } finally {
      setIsPreviewing(false);
    }
  };

  const uploadVideo = async (lessonID: string) => {
    if (!auth.churchId || !videoFile) {
      return;
    }

    setUploadProgress(0);
    setUploadError(null);
    setIsUploadComplete(false);

    const uploadTargetResponse = await TrainingController.createVideoUploadTarget(
      auth.churchId,
      props.moduleID,
      lessonID,
      auth
    );

    if (!uploadTargetResponse.body?.url) {
      setUploadError(uploadTargetResponse.error || t('uploadStartFailed'));
      return;
    }

    const uploadSucceeded = await NetworkController.performSignedPut(
      uploadTargetResponse.body.url,
      videoFile,
      videoFile.type,
      setUploadProgress
    );

    if (!uploadSucceeded) {
      setUploadError(t('uploadFailed'));
      return;
    }

    setIsUploadComplete(true);
  };

  const finishUpload = () => {
    setUploadingLessonID(null);
    props.onSaved();
  };

  const saveLesson = async () => {
    if (!auth.churchId) {
      setError(t('volunteering:errors.missingChurch'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let thumbnailURL = video?.thumbnailURL ?? null;

      if (thumbnailFile) {
        const thumbnailTargetResponse = await TrainingController.createThumbnailUploadTarget(
          auth.churchId,
          thumbnailFile.type || 'application/octet-stream',
          thumbnailFile.size,
          auth
        );

        if (!thumbnailTargetResponse.body) {
          setError(thumbnailTargetResponse.error || t('thumbnailTargetFailed'));
          setIsSaving(false);
          return;
        }

        const thumbnailUploaded = await NetworkController.performSignedPost(
          {
            url: thumbnailTargetResponse.body.url,
            fields: thumbnailTargetResponse.body.fields
          },
          thumbnailFile,
          thumbnailFile.type
        );

        if (!thumbnailUploaded) {
          setError(t('thumbnailUploadFailed'));
          setIsSaving(false);
          return;
        }

        thumbnailURL = thumbnailTargetResponse.body.assetURL;
      }

      const savedAttributes: TrainingResourceAttributes = {
        name: name.trim(),
        description: description.trim() || null,
        text: text.trim() || null,
        // A newly picked video sends only the type; the rest is filled in as
        // the upload progresses. An existing one is carried across untouched
        // apart from a thumbnail that was just replaced.
        video: videoFile ?
          { type: 'PROCESSING', thumbnailURL } :
          video ? { ...video, thumbnailURL } : null
      };

      const response = props.lesson ?
        await TrainingController.updateTrainingResource(
          auth.churchId,
          props.moduleID,
          props.lesson.id,
          savedAttributes,
          auth
        ) :
        await TrainingController.createTrainingResource(
          auth.churchId,
          props.moduleID,
          savedAttributes,
          auth
        );

      // An update already knows the ID, so it doesn't depend on the response
      // echoing the Lesson back.
      const savedLessonID = response.body?.result?.id ?? props.lesson?.id;

      if (response.error || !savedLessonID) {
        setError(response.error || t('saveFailed'));
        setIsSaving(false);
        return;
      }

      if (videoFile) {
        setUploadingLessonID(savedLessonID);
        setIsSaving(false);
        void uploadVideo(savedLessonID);
        return;
      }

      props.onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='lesson-editor-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='lesson-editor-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          // Closing mid-upload would abandon the transfer.
          disabled={isSaving || (isUploading && !isUploadComplete && !uploadError)}
          onClick={isUploading ? finishUpload : props.close}>
          <img src={closeIcon} alt={t('volunteering:common.close')} />
        </button>

        <h3 className='title'>
          {isUploading ?
            (isUploadComplete ? t('uploadedTitle') : t('uploadingTitle')) :
            (isEditing ? t('editTitle') : t('newTitle'))}
        </h3>

        {isUploading ? <span className='header-control-spacer' /> : <button
          type='button'
          className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
          disabled={!canSave}
          onClick={() => {
            void saveLesson();
          }}>
          {isSaving ?
            <span className='header-control-spinner' role='status' aria-label={t('savingLabel')} /> :
            <img src={checkmarkIcon} alt={t('saveLabel')} />}
        </button>}
      </div>

      <div className='body'>
        {isUploading ? <VideoUploadScreen
          fileName={videoFile?.name ?? t('fallbackFileName')}
          progress={uploadProgress}
          isComplete={isUploadComplete}
          error={uploadError}
          onRetry={() => {
            void uploadVideo(uploadingLessonID!);
          }}
          onDone={finishUpload}
        /> : <>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='field-container'>
          <label htmlFor='lesson-name'>{t('titleLabel')}</label>
          <input
            id='lesson-name'
            type='text'
            value={name}
            placeholder={t('titlePlaceholder')}
            disabled={isSaving}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='field-container'>
          <label htmlFor='lesson-description'>{t('descriptionLabel')}</label>
          <textarea
            id='lesson-description'
            rows={2}
            maxLength={DESCRIPTION_LIMIT}
            value={description}
            placeholder={t('descriptionPlaceholder')}
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className='caption character-count'>{description.length}/{DESCRIPTION_LIMIT}</p>
        </div>

        <div className='field-container'>
          <label>{t('videoLabel')}</label>
          <VideoPanel
            video={video}
            videoFile={videoFile}
            thumbnailFile={thumbnailFile}
            thumbnailURL={pickedThumbnailURL ?? video?.thumbnailURL ?? null}
            isSaving={isSaving}
            isPreviewing={isPreviewing}
            canPreview={isEditing}
            onPick={setVideoFile}
            onPickThumbnail={setThumbnailFile}
            onPreview={() => {
              void previewVideo();
            }}
          />
        </div>

        <div className='field-container'>
          <label htmlFor='lesson-text'>{t('textLabel')}</label>
          <div
            className='lesson-text-editor'
            onKeyDownCapture={swallowUnderlineShortcut}
            onPasteCapture={scrubUnderlineFromPaste}>
            <MDXEditor
              markdown={text}
              onChange={setText}
              // The editor's own toolbar, dialog and table strings, from this
              // screen's catalog. Anything not in it keeps the editor's English.
              translation={(key, defaultValue, interpolations) => {
                return t(`editor.${key}`, { defaultValue, ...interpolations });
              }}
              contentEditableClassName='lesson-text-content'
              className={theme === Theme.DARK ? 'dark-theme dark-editor' : undefined}
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                markdownShortcutPlugin(),
                // imagePlugin({
                //   imageUploadHandler: async (file) => {
                //     return ""
                //   }
                // }),
                tablePlugin(),
                toolbarPlugin({
                  toolbarContents: () => <>
                    <UndoRedo />
                    <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
                    <BlockTypeSelect />
                    <ListsToggle options={['bullet', 'number']} />
                    <CreateLink />
                    {/* <InsertImage /> */}
                    <InsertTable />
                  </>
                })
              ]}
            />
          </div>
        </div>
        </>}
      </div>
    </div>
  </Modal>
}

// The same platform split Lexical's own shortcut matcher uses.
const IS_APPLE = /Mac|iPod|iPhone|iPad/.test(navigator.platform);

// Mirrors Lexical's isUnderline: the command key on its own -- no shift, no
// alt -- plus the U key, falling back to the physical key on layouts that
// report something non-ASCII. Kept exact so nothing else gets swallowed.
function isUnderlineShortcut(event: React.KeyboardEvent<HTMLDivElement>) {
  const hasCommandKey = IS_APPLE ?
    event.metaKey && !event.ctrlKey :
    event.ctrlKey && !event.metaKey;

  if (!hasCommandKey || event.altKey || event.shiftKey) {
    return false;
  }

  if (event.key.toLowerCase() === 'u') {
    return true;
  }

  const isASCIIKey = event.key.length === 1 && event.key.charCodeAt(0) <= 127;

  return !isASCIIKey && event.code === 'KeyU';
}

// Cheap pre-check, so an ordinary paste is never rewritten.
const UNDERLINE_HTML_PATTERN = /<u[\s>]|text-decoration[^;"']*underline/i;

// Underline arrives from other apps as a <u> tag or a text-decoration style,
// either of which Lexical turns back into the <u> the shortcut used to produce.
function stripUnderlineFromHTML(html: string) {
  const parsed = new DOMParser().parseFromString(html, 'text/html');

  parsed.querySelectorAll('u').forEach((element) => {
    element.replaceWith(...Array.from(element.childNodes));
  });

  parsed.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
    const decoration = element.style.textDecorationLine || element.style.textDecoration;

    if (!decoration.toLowerCase().includes('underline')) return;

    // Only the underline is dropped: a run that's also struck through keeps it.
    const remaining = decoration.split(/\s+/).filter((value) => value.toLowerCase() !== 'underline').join(' ');

    element.style.removeProperty('text-decoration');
    element.style.removeProperty('text-decoration-line');

    if (remaining) {
      element.style.textDecoration = remaining;
    }

    if (!element.getAttribute('style')) {
      element.removeAttribute('style');
    }
  });

  return parsed.body.innerHTML;
}

// The replayed pastes below, so they pass straight through this handler rather
// than being scrubbed a second time.
const replayedPastes = new WeakSet<ClipboardEvent>();

// The paste is rewritten in the capture phase and replayed, so the editor only
// ever sees markup with the underline already gone.
function scrubUnderlineFromPaste(event: React.ClipboardEvent<HTMLDivElement>) {
  if (replayedPastes.has(event.nativeEvent)) return;

  const clipboard = event.clipboardData;
  const html = clipboard.getData('text/html');

  if (!html || !UNDERLINE_HTML_PATTERN.test(html)) return;

  const contentEditable = event.currentTarget.querySelector('[contenteditable="true"]');

  if (!contentEditable) return;

  event.preventDefault();
  event.stopPropagation();

  const scrubbed = new DataTransfer();
  scrubbed.setData('text/html', stripUnderlineFromHTML(html));
  scrubbed.setData('text/plain', clipboard.getData('text/plain'));
  // The editor's own clipboard payload is deliberately left out: Lexical reads
  // it in preference to text/html, which would put the underline right back.

  const replay = new ClipboardEvent('paste', {
    bubbles: true,
    cancelable: true,
    clipboardData: scrubbed
  });

  replayedPastes.add(replay);
  contentEditable.dispatchEvent(replay);
}

// Lexical binds the underline shortcut itself -- it isn't the markdown shortcut
// plugin -- and writes the result as a <u> tag, which this content doesn't
// render. Caught in the capture phase, so the editor's own keydown listener on
// the contenteditable never sees it.
function swallowUnderlineShortcut(event: React.KeyboardEvent<HTMLDivElement>) {
  if (!isUnderlineShortcut(event)) return;

  event.preventDefault();
  event.stopPropagation();
}

function VideoUploadScreen(props: {
  fileName: string;
  progress: number;
  isComplete: boolean;
  error: string | null;
  onRetry: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('lessonEditor');
  const percent = Math.round(props.progress * 100);

  return <div className='video-upload-screen'>
    <p className='video-upload-filename'>{props.fileName}</p>

    {props.error ? <>
      <p className='save-error'>{props.error}</p>
      <p className='caption'>{t('uploadSavedCaption')}</p>

      <div className='video-upload-actions'>
        <button type='button' className='video-upload-retry' onClick={props.onRetry}>{t('retryUpload')}</button>
        <button type='button' className='video-upload-done' onClick={props.onDone}>{t('close')}</button>
      </div>
    </> : <>
      <div
        className='video-upload-track'
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={props.isComplete ? 100 : percent}>
        <div className='video-upload-fill' style={{ width: `${props.isComplete ? 100 : percent}%` }} />
      </div>

      <p className='caption'>
        {props.isComplete ?
          t('uploadComplete') :
          t('uploading', { percent })}
      </p>
    </>}
  </div>
}

function VideoPanel(props: {
  video: TrainingVideo | null;
  videoFile: File | null;
  thumbnailFile: File | null;
  thumbnailURL: string | null;
  isSaving: boolean;
  isPreviewing: boolean;
  canPreview: boolean;
  onPick: (file: File | null) => void;
  onPickThumbnail: (file: File | null) => void;
  onPreview: () => void;
}) {
  const { t } = useTranslation('lessonEditor');
  const video = props.video;
  const isPlayable = video?.type === 'EXTERNAL' || video?.type === 'HOSTED';
  const hasVideo = Boolean(video || props.videoFile);

  const videoPicker = <div className='video-panel-picker'>
    <input
      id='lesson-video'
      type='file'
      accept='video/*'
      disabled={props.isSaving}
      onChange={(e) => props.onPick(e.target.files?.[0] ?? null)}
    />

    {props.videoFile ? <button
      type='button'
      className='video-panel-clear'
      disabled={props.isSaving}
      onClick={() => props.onPick(null)}>
      {video ? t('keepCurrentVideo') : t('clear')}
    </button> : null}
  </div>;

  const details = props.videoFile ?
    <div className='video-panel-details'>
      <p className='video-panel-platform'>{props.videoFile.name}</p>
      <p className='caption'>{t('uploadsAfterCreate')}</p>
    </div> :
    video ? <div className='video-panel-details'>
      <p className='video-panel-platform'>{videoHeadline(video)}</p>
      <p className='caption video-panel-url'>{videoDetail(video)}</p>

      {isPlayable && props.canPreview ? <button
        type='button'
        className='video-panel-preview'
        disabled={props.isPreviewing || props.isSaving}
        onClick={props.onPreview}>
        {props.isPreviewing ? t('preparingPreview') : t('previewVideo')}
      </button> : null}
    </div> :
    <p className='video-panel-copy'>{t('noVideo')}</p>;

  return <div className='video-panel-stack'>
    <div className={hasVideo ? 'video-panel' : 'video-panel empty'}>
      {details}
      {videoPicker}
    </div>

    {hasVideo ? <div className='video-panel thumbnail-row'>
      {props.thumbnailURL ?
        <img className='video-panel-thumbnail' src={props.thumbnailURL} alt='' aria-hidden='true' /> :
        <div className='video-panel-thumbnail empty' aria-hidden='true' />}

      <div className='video-panel-details'>
        <p className='video-panel-platform'>{t('thumbnailTitle')}</p>
        <p className='caption'>{t('thumbnailCaption')}</p>
      </div>

      <div className='video-panel-picker'>
        <input
          id='lesson-video-thumbnail'
          type='file'
          accept='image/*'
          disabled={props.isSaving}
          onChange={(e) => props.onPickThumbnail(e.target.files?.[0] ?? null)}
        />

        {props.thumbnailFile ? <button
          type='button'
          className='video-panel-clear'
          disabled={props.isSaving}
          onClick={() => props.onPickThumbnail(null)}>
          {video?.thumbnailURL ? t('keepCurrentImage') : t('clear')}
        </button> : null}
      </div>
    </div> : null}
  </div>
}

const PLATFORM_LABELS: Record<string, string> = {
  mux: 'Mux'
};

function videoHeadline(video: TrainingVideo) {
  switch (video.type) {
    case 'PROCESSING':
      return i18next.t('lessonEditor:videoProcessing');
    case 'EXTERNAL':
      return i18next.t('lessonEditor:videoUploaded');
    case 'HOSTED':
      return i18next.t('lessonEditor:videoHosted');
    case 'FAILED':
      return i18next.t('lessonEditor:videoUploadFailed');
  }
}

function videoDetail(video: TrainingVideo) {
  switch (video.type) {
    case 'PROCESSING':
      return video.assetID ?? video.uploadID ?? i18next.t('lessonEditor:videoProcessingDetail');
    case 'EXTERNAL':
      return null;
    case 'HOSTED':
      return video.url;
    case 'FAILED':
      return video.reason;
  }
}
